import React from "react";
import { LayoutGrid, Star, Plus, Folder, Rocket, Users, LogOut, ShieldCheck, KeyRound, ChevronsUpDown } from "lucide-react";
import { getIcon } from "@/lib/iconMap";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Sidebar = ({
  categories, activeFilter, onFilter, counts, onAddApp, user, onManageUsers, onLogout, onChangePassword,
}) => {
  const NavItem = ({ id, label, icon: Icon, count, colorClass }) => {
    const active = activeFilter === id;
    return (
      <button
        onClick={() => onFilter(id)}
        data-testid={`nav-${id}`}
        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
          active
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:bg-accent hover:text-foreground"
        }`}
      >
        <span className="flex items-center gap-3">
          <Icon className={`h-4 w-4 ${colorClass && !active ? colorClass : ""}`} />
          {label}
        </span>
        {count !== undefined && (
          <span className={`text-xs ${active ? "text-primary-foreground/80" : "text-muted-foreground/70"}`}>
            {count}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside className="fixed left-0 top-0 z-30 flex h-screen w-64 flex-col gap-1 border-r border-border bg-white/70 p-4 backdrop-blur-xl dark:bg-black/50">
      <div className="mb-4 flex items-center gap-2.5 px-2 py-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow">
          <Rocket className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-base font-extrabold tracking-tight">Launchpad</h1>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Web App Hub</p>
        </div>
      </div>

      <button
        onClick={onAddApp}
        data-testid="add-app-button"
        className="mb-3 flex items-center justify-center gap-2 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground shadow transition-transform duration-150 hover:brightness-110 active:scale-95"
      >
        <Plus className="h-4 w-4" /> Add App
      </button>

      <NavItem id="all" label="All Apps" icon={LayoutGrid} count={counts.all} />
      <NavItem id="favorites" label="Favorites" icon={Star} count={counts.favorites} colorClass="text-amber-400" />

      <div className="mt-4 mb-2 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        Categories
      </div>
      <ScrollArea className="flex-1 -mx-1 px-1">
        <div className="flex flex-col gap-1">
          {categories.map((cat) => (
            <NavItem
              key={cat}
              id={`cat:${cat}`}
              label={cat}
              icon={Folder}
              count={counts.categories[cat]}
            />
          ))}
        </div>
      </ScrollArea>

      <div className="mt-2 border-t border-border pt-3">
        {user?.role === "admin" && (
          <button onClick={onManageUsers} data-testid="manage-users-button"
            className="mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
            <Users className="h-4 w-4" /> Manage Users
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="profile-menu-trigger"
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-accent">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white ${user?.role === "admin" ? "bg-primary" : "bg-zinc-500"}`}>
                {user?.role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <span className="text-xs font-bold">{(user?.name || "?")[0]?.toUpperCase()}</span>}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top" className="w-52">
            <DropdownMenuItem onClick={onChangePassword} data-testid="change-password-menu-item">
              <KeyRound className="mr-2 h-4 w-4" /> Change password
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout} data-testid="logout-button"
              className="text-destructive focus:text-destructive">
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
};
